import { useEffect, useRef, useState } from 'react';
import { FormApi } from 'informed';
import { getResizedImage } from 'shared-helpers/common';
import api from 'api';
import { useDispatch, useReduxState } from 'hooks';
import { waitForImage } from 'lib/files';
import { Alert, Button } from 'components/common';
import { Box } from 'components/common/box';
import { DisplayField, Form, FormSection } from 'components/common/form';
import { UnsavedFormChanges } from 'components/common/notifications';
import {
  HomePageSettingsBanner,
  HomePageSettingsBannerThemes,
  HomePageSettingsModules,
  HomePageSettingsModulesModule,
  HomePageSettingsSummary,
  useHomePageSettingsContext
} from '.';

import type { HomePageModule } from 'shared-types';
import type { UpdateHomePageModulesReq } from 'shared-types/requests';
import type { GetHomePageModulesRes } from 'shared-types/responses';

type Modules = Omit<GetHomePageModulesRes['modules'][0], 'type'>;

export type HomePageSettingsFormValues = UpdateHomePageModulesReq['config'] & {
  homePageModules: (Partial<Modules> & { type: HomePageModule['type'] })[];
};

const HomePageSettings = () => {
  const { errors } = useHomePageSettingsContext();

  const { dispatch } = useDispatch();

  const { messages, serverConfig } = useReduxState();

  const formApi = useRef<FormApi<HomePageSettingsFormValues>>(undefined);
  const bannerImageUploader = useRef<any>(undefined);
  const checkerTimeout = useRef<NodeJS.Timeout>(undefined);

  const [enableFormChecker, setEnableFormChecker] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    checkerTimeout.current = setTimeout(() => {
      dispatch.updatePageState({ refreshInitialState: true });
      setEnableFormChecker(true);
    }, 1000);
    return () => clearTimeout(checkerTimeout.current);
  }, [dispatch]);

  const handleSubmit = async () => {
    setIsLoading(true);

    const { values } = formApi.current.getState();

    const payload = {} as UpdateHomePageModulesReq;
    try {
      if (bannerImageUploader.current?.state.file) {
        const file = await bannerImageUploader.current.upload();
        if (file) {
          values.homePageBannerImage = file.url;

          // wait for resized image to be available
          await waitForImage(getResizedImage({ size: 1920, url: file.url }));
        }
      }
      if (!values.homePageBannerImage) values.homePageBannerImage = null;

      const homePageInfoChanged =
        values.homePageInfoEnabled !== serverConfig.homePageInfoEnabled ||
        values.homePageInfoText !== serverConfig.homePageInfoText ||
        values.homePageInfoTitle !== serverConfig.homePageInfoTitle ||
        JSON.stringify(values.homePageInfoTextTranslations) !==
          JSON.stringify(serverConfig.homePageInfoTextTranslations) ||
        JSON.stringify(values.homePageInfoTitleTranslations) !==
          JSON.stringify(serverConfig.homePageInfoTitleTranslations);

      payload.config = {
        homePageBannerImage: values.homePageBannerImage,
        homePageBannerImageLayout: values.homePageBannerImageLayout,
        homePageBannerImagePosition: values.homePageBannerImagePosition,
        homePageInfoEnabled: values.homePageInfoEnabled,
        homePageInfoText: values.homePageInfoText,
        homePageInfoTextTranslations: values.homePageInfoTextTranslations,
        homePageInfoTitle: values.homePageInfoTitle,
        homePageInfoTitleTranslations: values.homePageInfoTitleTranslations,
        homePageQuickLinks: values.homePageQuickLinks,
        homePageShowExpiringCompletions: values.homePageShowExpiringCompletions,
        homePageShowMyCourses: values.homePageShowMyCourses,
        homePageShowMyPaths: values.homePageShowMyPaths,
        homePageShowPendingRequests: values.homePageShowPendingRequests,
        homePageShowUnreadChatMessages: values.homePageShowUnreadChatMessages,
        homePageShowUnreadFeedbacks: values.homePageShowUnreadFeedbacks,
        homePageShowUnreviewedAnswers: values.homePageShowUnreviewedAnswers,
        homePageShowUpcomingDeadlines: values.homePageShowUpcomingDeadlines,
        homePageShowUpcomingEvents: values.homePageShowUpcomingEvents,
        ...(homePageInfoChanged
          ? { homePageInfoUpdatedTimestamp: Date.now() }
          : {})
      };

      if (serverConfig.platformCompetenciesOnly) {
        payload.modules = null; // avoid wiping out defaults from the DB
      } else {
        payload.modules = values.homePageModules.map((module) => ({
          data: module.data || {},
          description: module.description || null,
          descriptionTranslations: module.descriptionTranslations || {},
          limit: module.limit || 0,
          products: {
            ...(module.categories?.length
              ? { categories: module.categories }
              : module.courses?.length
              ? { courses: module.courses }
              : module.events?.length
              ? { events: module.events }
              : module.paths?.length
              ? { paths: module.paths }
              : null)
          },
          title: module.title || null,
          titleTranslations: module.titleTranslations || {},
          type: module.type
        }));
      }

      const res = await api.put('/admin/home-page', payload);
      if (res.data.success) {
        dispatch.setServerConfig({ ...serverConfig, ...payload.config });
        dispatch.updatePageState({ refreshInitialState: true });
      }

      dispatch.addNotice({
        text: messages.settingsSavedSuccessfully,
        type: 'success'
      });
    } catch (err) {
      await dispatch.handleErrorDispatch(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateQuickLinks = (index: number, newModule: Modules) => {
    const formState = formApi.current.getState();
    formApi.current.setValue('homePageQuickLinks', {
      description: newModule.description,
      descriptionTranslations: newModule.descriptionTranslations,
      title: newModule.title,
      titleTranslations: newModule.titleTranslations,
      links: newModule.data?.links
        ? JSON.parse(JSON.stringify(newModule.data.links))
        : formState.values.homePageQuickLinks?.links
    });
  };

  const initialValues: HomePageSettingsFormValues = {
    homePageBannerImage: serverConfig.homePageBannerImage || '',
    homePageBannerImageLayout:
      serverConfig.homePageBannerImageLayout || 'full-width',
    homePageBannerImagePosition:
      serverConfig.homePageBannerImagePosition || 'center center',
    homePageInfoEnabled: serverConfig.homePageInfoEnabled,
    homePageInfoText: serverConfig.homePageInfoText,
    homePageInfoTextTranslations: serverConfig.homePageInfoTextTranslations,
    homePageInfoTitle: serverConfig.homePageInfoTitle,
    homePageInfoTitleTranslations: serverConfig.homePageInfoTitleTranslations,
    homePageModules: [],
    homePageQuickLinks: serverConfig.homePageQuickLinks,
    homePageShowExpiringCompletions:
      serverConfig.homePageShowExpiringCompletions,
    homePageShowMyCourses: serverConfig.homePageShowMyCourses,
    homePageShowMyPaths: serverConfig.homePageShowMyPaths,
    homePageShowPendingRequests: serverConfig.homePageShowPendingRequests,
    homePageShowUnreadChatMessages: serverConfig.homePageShowUnreadChatMessages,
    homePageShowUnreadFeedbacks: serverConfig.homePageShowUnreadFeedbacks,
    homePageShowUnreviewedAnswers: serverConfig.homePageShowUnreviewedAnswers,
    homePageShowUpcomingDeadlines: serverConfig.homePageShowUpcomingDeadlines,
    homePageShowUpcomingEvents: serverConfig.homePageShowUpcomingEvents
  };

  const errorCount = Object.keys(errors).reduce((numberOfErrors, module) => {
    return errors[module] ? numberOfErrors + 1 : numberOfErrors;
  }, 0);

  return (
    <Box padding={0} title={messages.homePage}>
      <Form
        allowEmptyStrings={true}
        getApi={(form) => (formApi.current = form)}
        initialValues={initialValues}
        onSubmit={handleSubmit}
      >
        {({ formState }) => (
          <>
            {enableFormChecker ? (
              <UnsavedFormChanges
                detectRemovedItems={true}
                initialValues={initialValues}
                styles={{ margin: '10px 15px 10px 15px' }}
              />
            ) : null}

            <div>
              <FormSection
                maxWidth="100%"
                title={messages.homePageSettingsBanner}
              >
                <HomePageSettingsBanner
                  setUploader={(uploader) =>
                    (bannerImageUploader.current = uploader)
                  }
                />

                <HomePageSettingsBannerThemes />
              </FormSection>

              <FormSection
                maxWidth="100%"
                title={messages.homePageSettingsUserSection}
              >
                <HomePageSettingsSummary />
              </FormSection>

              <FormSection
                maxWidth="100%"
                title={messages.homePageModuleQuickLinksTitle}
                tooltip={messages.homePageModuleQuickLinksBlurb}
              >
                <HomePageSettingsModulesModule
                  index={-10}
                  module={{
                    data: {
                      links: formState.values.homePageQuickLinks?.links || []
                    },
                    description:
                      formState.values.homePageQuickLinks?.description,
                    descriptionTranslations:
                      formState.values.homePageQuickLinks
                        ?.descriptionTranslations,
                    limit: 1000,
                    type: 'quick-links',
                    title: formState.values.homePageQuickLinks?.title,
                    titleTranslations:
                      formState.values.homePageQuickLinks?.titleTranslations
                  }}
                  updateModule={handleUpdateQuickLinks}
                />
              </FormSection>

              {!serverConfig.platformCompetenciesOnly && (
                <FormSection
                  maxWidth="100%"
                  title={messages.homePageSettingsModulesSection}
                >
                  <HomePageSettingsModules />
                </FormSection>
              )}

              <DisplayField field="homePageQuickLinks" />
              {!serverConfig.platformCompetenciesOnly && (
                <DisplayField field="homePageModules" />
              )}
            </div>
          </>
        )}
      </Form>

      <FormSection maxWidth="100%">
        <div style={{ display: 'flex', gap: 10 }}>
          <Button
            category="success"
            disabled={errorCount > 0}
            loading={isLoading}
            onClick={handleSubmit}
            text={messages.commonSaveChanges}
          />

          {errorCount > 0 ? (
            <Alert type="error-light" text={messages.commonErrorInForm} />
          ) : null}
        </div>
      </FormSection>
    </Box>
  );
};

export default HomePageSettings;
